/**
 * Unified production-grade logger utility
 * Combines category-based filtering with log levels
 * Configurable at runtime via localStorage
 */

import { config } from '@/config';
import { getStorageItem, setStorageItem, STORAGE_KEYS } from '@/core/utils/local-storage';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogCategory {
  // Core features
  auth: boolean;
  api: boolean;
  socket: boolean;
  navigation: boolean;
  
  // Data & State
  redux: boolean;
  rtk: boolean;
  conversations: boolean;
  messages: boolean;
  
  // UI
  components: boolean;
  
  // Development
  transforms: boolean;
  verbose: boolean;
}

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  categories: LogCategory;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

// Development logging configuration
const developmentCategories: LogCategory = {
  // Core features - show important logs
  auth: false, // Too noisy, only show errors
  api: false, // Too noisy, only show errors
  socket: false, // Too noisy, only show errors
  navigation: false,
  
  // Data & State - hide verbose logs
  redux: false,
  rtk: false,
  conversations: false,
  messages: false,
  
  // UI - hide verbose logs
  components: false,
  
  // Development - disable by default
  transforms: false,
  verbose: false,
};

// Production logging configuration
const productionCategories: LogCategory = {
  // Only show critical errors in production
  auth: false,
  api: false,
  socket: false,
  navigation: false,
  redux: false,
  rtk: false,
  conversations: false,
  messages: false,
  components: false,
  transforms: false,
  verbose: false,
};

class Logger {
  private context: string;
  private config: LoggerConfig;
  private static globalConfig: LoggerConfig = {
    enabled: config.features.debugLogging,
    level: 'debug',
    categories: config.env === 'production' 
      ? productionCategories 
      : developmentCategories
  };

  constructor(context: keyof LogCategory | string) {
    this.context = context;
    this.config = Logger.globalConfig;
    
    // Load overrides from localStorage on initialization
    if (typeof window !== 'undefined' && !Logger.configLoaded) {
      Logger.loadConfigFromStorage();
      Logger.configLoaded = true;
    }
  }

  private static configLoaded = false;

  private static loadConfigFromStorage(): void {
    try {
      const overrides = getStorageItem(STORAGE_KEYS.LOGGER_CONFIG) as { categories?: Partial<LogCategory>; level?: LogLevel; enabled?: boolean } | null;
      if (overrides) {
        if (overrides.categories) {
          Object.assign(Logger.globalConfig.categories, overrides.categories);
        }
        if (overrides.level) {
          Logger.globalConfig.level = overrides.level;
        }
        if (overrides.enabled !== undefined) {
          Logger.globalConfig.enabled = overrides.enabled;
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }

  private shouldLog(level: LogLevel): boolean {
    // Always log in test environment
    if (config.env === 'test') return true;
    
    // Check if logging is globally enabled
    if (!this.config.enabled) return false;
    
    // Check log level
    if (LOG_LEVELS[level] < LOG_LEVELS[this.config.level]) return false;
    
    // Check category if it exists
    if (this.context in this.config.categories) {
      const categoryEnabled = this.config.categories[this.context as keyof LogCategory];
      // For errors, always log if category exists (regardless of category setting)
      if (level === 'error') return true;
      return categoryEnabled;
    }
    
    // For unknown categories, respect verbose setting
    return this.config.categories.verbose;
  }

  private static output(level: LogLevel, message: string, ...args: unknown[]): void {
    // Base output method that doesn't use Logger instances to avoid recursion
    // Only outputs if logging is enabled globally
    if (!Logger.globalConfig.enabled) return;
    
    switch (level) {
      case 'debug':
      case 'info':
        console.log(message, ...args);
        break;
      case 'warn':
        console.warn(message, ...args);
        break;
      case 'error':
        console.error(message, ...args);
        break;
    }
  }

  private formatMessage(level: LogLevel, message: string, ...args: unknown[]): void {
    const timestamp = new Date().toISOString();
    const levelStr = level.toUpperCase().padEnd(5);
    const contextStr = `[${this.context}]`.padEnd(15);
    
    const formattedMessage = config.env === 'development'
      ? `${contextStr} ${message}` // Simpler format for development
      : `${timestamp} ${contextStr} [${levelStr}] ${message}`; // Full format for production
    
    // Use the static output method to avoid recursion
    Logger.output(level, formattedMessage, ...args);
  }

  log(...args: unknown[]): void {
    this.info(String(args[0]), ...args.slice(1));
  }

  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      this.formatMessage('debug', message, ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info')) {
      this.formatMessage('info', message, ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      this.formatMessage('warn', message, ...args);
    }
  }

  error(message: string, ...args: unknown[]): void {
    // Errors always log if the category exists or verbose is on
    if (this.shouldLog('error')) {
      this.formatMessage('error', message, ...args);
    }
  }

  // Create a child logger with a specific context
  child(context: string): Logger {
    return new Logger(`${this.context}:${context}`);
  }

  // Static methods for runtime configuration
  static setCategories(overrides: Partial<LogCategory>): void {
    Object.assign(Logger.globalConfig.categories, overrides);
    Logger.saveConfigToStorage();
  }

  static setLevel(level: LogLevel): void {
    Logger.globalConfig.level = level;
    Logger.saveConfigToStorage();
  }

  static setEnabled(enabled: boolean): void {
    Logger.globalConfig.enabled = enabled;
    Logger.saveConfigToStorage();
  }

  static enableCategory(category: keyof LogCategory): void {
    Logger.globalConfig.categories[category] = true;
    Logger.saveConfigToStorage();
  }

  static disableCategory(category: keyof LogCategory): void {
    Logger.globalConfig.categories[category] = false;
    Logger.saveConfigToStorage();
  }

  static enableAllCategories(): void {
    Object.keys(Logger.globalConfig.categories).forEach(key => {
      Logger.globalConfig.categories[key as keyof LogCategory] = true;
    });
    Logger.saveConfigToStorage();
  }

  static disableAllCategories(): void {
    Object.keys(Logger.globalConfig.categories).forEach(key => {
      Logger.globalConfig.categories[key as keyof LogCategory] = false;
    });
    Logger.saveConfigToStorage();
  }

  static getConfig(): LoggerConfig {
    return { ...Logger.globalConfig };
  }

  private static saveConfigToStorage(): void {
    try {
      setStorageItem(STORAGE_KEYS.LOGGER_CONFIG, {
        categories: Logger.globalConfig.categories,
        level: Logger.globalConfig.level,
        enabled: Logger.globalConfig.enabled
      });
    } catch {
      // Ignore localStorage errors
    }
  }
}

// Export named loggers for specific modules
export const authLogger = new Logger('auth');
export const apiLogger = new Logger('api');
export const socketLogger = new Logger('socket');
export const navigationLogger = new Logger('navigation');
export const reduxLogger = new Logger('redux');
export const conversationsLogger = new Logger('conversations');
export const messagesLogger = new Logger('messages');
export const componentsLogger = new Logger('components');

// Export the Logger class for creating custom loggers
export { Logger };

// Export default logger for general use
const defaultLogger = new Logger('app');
export default defaultLogger;

// Development helpers exposed on window object
if (typeof window !== 'undefined' && config.env === 'development') {
  window.logger = {
    enable: () => Logger.setEnabled(true),
    disable: () => Logger.setEnabled(false),
    setLevel: (level: LogLevel) => Logger.setLevel(level),
    enableCategory: (category: keyof LogCategory) => Logger.enableCategory(category),
    disableCategory: (category: keyof LogCategory) => Logger.disableCategory(category),
    enableAll: () => Logger.enableAllCategories(),
    disableAll: () => Logger.disableAllCategories(),
    getConfig: () => Logger.getConfig(),
    categories: developmentCategories,
  };
  console.log('[redux]          [Logger] Development logger helpers available on window.logger');
}
